import React, { useEffect, useRef, useCallback } from 'react';
import Hls from 'hls.js';
import { Lock } from 'lucide-react';

/**
 * Plays whatever the production backend returns for a lesson (already entitlement-gated
 * server-side). Handles:
 *   - Bunny.net signed iframe embed  (embedUrl, or a mediadelivery.net/embed playbackUrl)
 *   - HLS streams                    (.m3u8 — Bunny or Cloudflare — via hls.js / native Safari)
 *   - Direct MP4                     (native <video>)
 * The frontend never fetches a separate playback endpoint or decides entitlement — it simply
 * renders the URL the backend chose to expose.
 */
function classify(playbackUrl, embedUrl) {
  if (embedUrl) return { kind: 'iframe', src: embedUrl };
  if (!playbackUrl) return { kind: 'none' };
  const u = playbackUrl;
  if (u.includes('mediadelivery.net/embed') || /\/embed\//.test(u)) return { kind: 'iframe', src: u };
  if (u.includes('.m3u8')) return { kind: 'hls', src: u };
  return { kind: 'file', src: u };
}

export function VideoPlayer({ playbackUrl, embedUrl, poster, resumeAt = 0, onTime, locked }) {
  const videoRef = useRef(null);
  const lastReport = useRef(0);
  const media = classify(playbackUrl, embedUrl);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || media.kind === 'iframe' || media.kind === 'none') return;
    let hls;
    if (media.kind === 'hls') {
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = media.src;
      } else if (Hls.isSupported()) {
        hls = new Hls({ enableWorker: true });
        hls.loadSource(media.src);
        hls.attachMedia(video);
      }
    } else {
      video.src = media.src;
    }
    return () => {
      if (hls) hls.destroy();
    };
  }, [media.kind, media.src]);

  const handleLoaded = useCallback(() => {
    const video = videoRef.current;
    if (video && resumeAt > 0 && resumeAt < (video.duration || Infinity)) {
      video.currentTime = resumeAt;
    }
  }, [resumeAt]);

  const handleTime = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const now = Date.now();
    if (now - lastReport.current > 5000) {
      lastReport.current = now;
      onTime && onTime(Math.floor(video.currentTime));
    }
  }, [onTime]);

  const handleEnded = useCallback(() => {
    const video = videoRef.current;
    if (video) onTime && onTime(Math.floor(video.duration || video.currentTime), true);
  }, [onTime]);

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="video-player">
      {locked || media.kind === 'none' ? (
        <div className="grid h-full w-full place-items-center bg-ink-card text-center">
          <div className="space-y-3 px-6">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-emerald/10 text-emerald">
              <Lock />
            </div>
            <p className="font-heading text-lg text-white">
              {locked ? 'Video locked' : 'Video unavailable'}
            </p>
            <p className="text-sm text-zinc-400">
              {locked ? 'Unlock full access through your Whop membership.' : 'No playable source for this lesson yet.'}
            </p>
          </div>
        </div>
      ) : media.kind === 'iframe' ? (
        <iframe
          key={media.src}
          src={media.src}
          title="Lesson video"
          className="h-full w-full border-0"
          loading="lazy"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          data-testid="bunny-iframe"
        />
      ) : (
        <video
          ref={videoRef}
          poster={poster}
          controls
          playsInline
          className="h-full w-full bg-black"
          onLoadedMetadata={handleLoaded}
          onTimeUpdate={handleTime}
          onEnded={handleEnded}
          data-testid="html5-video"
        />
      )}
    </div>
  );
}
